import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Send,
  Loader2,
  MessageSquare,
  Clock,
  ExternalLink,
  Phone,
  MapPin,
  FileText,
  CheckCheck,
  AlertCircle,
  Headphones,
  Plus,
  Search,
  X,
  ArrowLeft,
} from "lucide-react";
import AccountManagerChat from "@/components/dashboard/AccountManagerChat";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAgent } from "@/contexts/AgentContext";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";

// ── Types ────────────────────────────────────────────────────────

interface Conversation {
  leadId: string;
  leadName: string;
  leadType: string;
  leadState: string;
  leadPhone: string;
  lastMessage: string;
  lastDirection: "inbound" | "outbound";
  lastStatus: string;
  lastTime: number;
  messageCount: number;
}

interface SmsMessage {
  id: string;
  direction: "inbound" | "outbound";
  body: string;
  status: string;
  created_at: number;
}

// ── Helpers ──────────────────────────────────────────────────────

function getLeadName(profile: Record<string, unknown>): string {
  const first = String(profile.first_name || "").trim();
  const last = String(profile.last_name || "").trim();
  if (first || last) return `${first} ${last}`.trim();
  return "Unknown";
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function formatRelativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatMessageTime(ts: number): string {
  const date = new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  if (diffDays === 0) return time;
  if (diffDays === 1) return `Yesterday ${time}`;
  if (diffDays < 7) {
    return `${date.toLocaleDateString("en-US", { weekday: "short" })} ${time}`;
  }
  return `${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })} ${time}`;
}

function formatPhoneDisplay(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case "delivered":
      return <CheckCheck className="w-3 h-3 text-green-500" />;
    case "sent":
    case "queued":
      return <Clock className="w-3 h-3 text-muted-foreground" />;
    case "failed":
    case "undelivered":
      return <AlertCircle className="w-3 h-3 text-red-500" />;
    default:
      return null;
  }
}

// ── Account Manager — fetched from DB or fallback ────────────────

const DEFAULT_AM = {
  id: "",
  name: "Your Account Manager",
  initials: "TJ",
  welcomeMessage:
    "Welcome to Trusted James! Your dedicated account manager will be available here soon. You'll be able to ask questions about your leads, billing, delivery optimization, and anything else you need help with.",
};

// ── Component ────────────────────────────────────────────────────

const Messages = () => {
  const isMobile = useIsMobile();
  const { state: agentState } = useAgent();
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const leadParam = searchParams.get("lead");
  // Capture leadParam in a ref so it survives URL cleanup re-renders
  const initialLeadRef = useRef<string | null>(leadParam);

  // Conversation list state
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(true);

  const [selectedId, setSelectedId] = useState<string>(initialLeadRef.current || "account-manager");
  const { markConversationAsRead } = useUnreadMessages();

  const selectConversation = useCallback((id: string) => {
    setSelectedId(id);
    setMobileShowChat(true);
    // Mark inbound messages as read when opening a lead conversation
    if (id !== "account-manager") {
      markConversationAsRead(id);
    }
  }, [markConversationAsRead]);

  // Thread state (for lead conversations)
  const [messages, setMessages] = useState<SmsMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // New conversation: lead opened from LeadDetail or "New Message" picker with no prior messages
  const [newConversation, setNewConversation] = useState<Conversation | null>(null);

  // "New Message" picker state
  const [showNewMessagePicker, setShowNewMessagePicker] = useState(false);
  const [availableLeads, setAvailableLeads] = useState<Conversation[]>([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [leadSearchQuery, setLeadSearchQuery] = useState("");

  // Compose state
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // ── Fetch assigned AM from DB ──────────────────────────────────
  const [amInfo, setAmInfo] = useState(DEFAULT_AM);

  useEffect(() => {
    const fetchAM = async () => {
      try {
        const agentId = agentState.profile.id;
        if (!agentId) return;

        const { data: assignment } = await supabase
          .from("client_am_assignments")
          .select("am_id")
          .eq("agent_id", agentId)
          .eq("status", "active")
          .maybeSingle();

        if (!assignment) return;

        const { data: am } = await supabase
          .from("account_managers")
          .select("id, full_name")
          .eq("id", assignment.am_id)
          .maybeSingle();

        if (am && am.full_name) {
          const name = am.full_name;
          const parts = name.split(" ").filter(Boolean);
          const initials = parts.length >= 2
            ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
            : (parts[0]?.[0] || "A").toUpperCase() + "M";
          setAmInfo({
            id: am.id,
            name,
            initials,
            welcomeMessage: `Hi! I'm ${name}, your dedicated account manager at Trusted James. Feel free to reach out anytime — I'm here to help with leads, billing, delivery optimization, and anything else you need.`,
          });
        }
      } catch {
        // Keep default fallback
      }
    };
    fetchAM();
  }, [agentState.profile.id]);

  // ── Helper: create a new conversation entry from a lead ──────────

  const createNewConversationFromLead = useCallback((lead: {
    id: string;
    lead_type_name: string;
    state: string;
    lead_profile: Record<string, unknown> | null;
  }) => {
    const profile = (lead.lead_profile ?? {}) as Record<string, unknown>;
    const conv: Conversation = {
      leadId: lead.id,
      leadName: getLeadName(profile),
      leadType: lead.lead_type_name,
      leadState: lead.state,
      leadPhone: String(profile.phone || ""),
      lastMessage: "",
      lastDirection: "outbound",
      lastStatus: "",
      lastTime: Date.now(),
      messageCount: 0,
    };
    setNewConversation(conv);
    selectConversation(lead.id);
    setShowNewMessagePicker(false);
    setLeadSearchQuery("");
  }, []);

  // ── Fetch lead for new conversation (from ?lead=:id URL param) ───

  useEffect(() => {
    const targetLeadId = initialLeadRef.current;
    if (!targetLeadId) return;
    // Only run once
    initialLeadRef.current = null;

    const fetchLeadForNewConversation = async () => {
      const { data: lead } = await supabase
        .from("leads")
        .select("id, lead_type_name, state, lead_profile")
        .eq("id", targetLeadId)
        .maybeSingle();

      if (!lead) return;
      createNewConversationFromLead({ ...lead, lead_profile: (lead.lead_profile as Record<string, unknown>) ?? {} });
    };

    fetchLeadForNewConversation();
  }, [createNewConversationFromLead]);

  // Clear newConversation once a real conversation exists for this lead
  useEffect(() => {
    if (newConversation && conversations.some((c) => c.leadId === newConversation.leadId)) {
      setNewConversation(null);
    }
  }, [conversations, newConversation]);

  // ── Agent ID (viewAs-aware) ──────────────────────────────────────

  const agentId = agentState.profile.id;

  // ── Fetch available leads for "New Message" picker ───────────────

  const fetchAvailableLeads = useCallback(async () => {
    if (!agentId) return;
    setLoadingLeads(true);

    const { data: leads } = await supabase
      .from("leads")
      .select("id, lead_type_name, state, lead_profile")
      .eq("routed_to_agent_id", agentId)
      .order("created_at", { ascending: false })
      .limit(100);

    if (!leads) {
      setLoadingLeads(false);
      return;
    }

    // Filter out leads that already have conversations
    const existingLeadIds = new Set(conversations.map((c) => c.leadId));
    const newLeads = leads.filter((l) => !existingLeadIds.has(l.id));

    setAvailableLeads(
      newLeads.map((l) => {
        const profile = (l.lead_profile ?? {}) as Record<string, unknown>;
        return {
          leadId: l.id,
          leadName: getLeadName(profile),
          leadType: l.lead_type_name,
          leadState: l.state,
          leadPhone: String(profile.phone || ""),
          lastMessage: "",
          lastDirection: "outbound" as const,
          lastStatus: "",
          lastTime: 0,
          messageCount: 0,
        };
      }),
    );
    setLoadingLeads(false);
  }, [conversations, agentId]);

  // ── Fetch conversation list ────────────────────────────────────

  const fetchConversations = useCallback(async () => {
    if (!agentId) return;

    const { data: comms, error: commsError } = await supabase
      .from("communications")
      .select("lead_id, body, direction, status, created_at")
      .eq("channel", "sms")
      .eq("agent_id", agentId)
      .order("created_at", { ascending: false });

    if (commsError || !comms) {
      console.error("[Messages] Failed to fetch communications:", commsError);
      setLoadingConversations(false);
      return;
    }

    // Group by lead_id — first occurrence per lead = most recent message
    const convMap = new Map<
      string,
      { lastBody: string; lastDir: string; lastStatus: string; lastTime: number; count: number }
    >();
    for (const c of comms) {
      if (c.lead_id === "unmatched") continue;
      if (!convMap.has(c.lead_id)) {
        convMap.set(c.lead_id, {
          lastBody: c.body || "",
          lastDir: c.direction,
          lastStatus: c.status,
          lastTime: c.created_at,
          count: 1,
        });
      } else {
        convMap.get(c.lead_id)!.count++;
      }
    }

    // Fetch lead profiles for all lead_ids
    const leadIds = Array.from(convMap.keys());
    if (leadIds.length === 0) {
      setConversations([]);
      setLoadingConversations(false);
      return;
    }

    const { data: leads } = await supabase
      .from("leads")
      .select("id, lead_type_name, state, lead_profile")
      .in("id", leadIds);

    const leadMap = new Map<string, { name: string; type: string; state: string; phone: string }>();
    for (const l of leads ?? []) {
      const profile = (l.lead_profile ?? {}) as Record<string, unknown>;
      leadMap.set(l.id, {
        name: getLeadName(profile),
        type: l.lead_type_name,
        state: l.state,
        phone: String(profile.phone || ""),
      });
    }

    // Build sorted conversation list
    const convList: Conversation[] = [];
    for (const [leadId, conv] of convMap) {
      const lead = leadMap.get(leadId);
      convList.push({
        leadId,
        leadName: lead?.name ?? "Unknown Lead",
        leadType: lead?.type ?? "",
        leadState: lead?.state ?? "",
        leadPhone: lead?.phone ?? "",
        lastMessage: conv.lastBody,
        lastDirection: conv.lastDir as "inbound" | "outbound",
        lastStatus: conv.lastStatus,
        lastTime: conv.lastTime,
        messageCount: conv.count,
      });
    }
    convList.sort((a, b) => b.lastTime - a.lastTime);
    setConversations(convList);
    setLoadingConversations(false);
  }, [agentId]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Clear ?lead= param after consuming it (keeps URL clean)
  useEffect(() => {
    if (leadParam) {
      setSearchParams({}, { replace: true });
    }
  }, [leadParam, setSearchParams]);

  // ── Realtime: refresh conversation list on any change ──────────

  useEffect(() => {
    const channel = supabase
      .channel("messages-conversations")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "communications" },
        () => {
          fetchConversations();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchConversations]);

  // ── Fetch thread messages for selected lead ────────────────────

  const fetchMessages = useCallback(async (leadId: string) => {
    if (!agentId) return;
    setLoadingMessages(true);
    const { data, error } = await supabase
      .from("communications")
      .select("id, direction, body, status, created_at")
      .eq("lead_id", leadId)
      .eq("channel", "sms")
      .eq("agent_id", agentId)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("[Messages] Failed to fetch thread:", error);
    } else {
      setMessages((data ?? []) as SmsMessage[]);
    }
    setLoadingMessages(false);
  }, [agentId]);

  // When selected conversation changes, fetch messages and mark as read
  useEffect(() => {
    if (selectedId && selectedId !== "account-manager") {
      fetchMessages(selectedId);
      markConversationAsRead(selectedId);
      setBody("");
    } else {
      setMessages([]);
    }
  }, [selectedId, fetchMessages, markConversationAsRead]);

  // Realtime for selected thread
  useEffect(() => {
    if (!selectedId || selectedId === "account-manager") return;

    const channel = supabase
      .channel(`messages-thread-${selectedId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "communications",
          filter: `lead_id=eq.${selectedId}`,
        },
        () => {
          fetchMessages(selectedId);
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedId, fetchMessages]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // ── Send SMS ───────────────────────────────────────────────────

  const handleSend = async () => {
    const trimmed = body.trim();
    if (!trimmed || sending || !selectedId || selectedId === "account-manager") return;

    setSending(true);
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) {
        toast.error("Session expired. Please refresh the page.");
        setSending(false);
        return;
      }

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-sms`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ leadId: selectedId, body: trimmed }),
        },
      );

      const result = await res.json();
      if (!res.ok) {
        toast.error(result.error || "Failed to send SMS");
      } else {
        setBody("");
        toast.success("SMS sent");
        await fetchMessages(selectedId);
      }
    } catch (err) {
      console.error("[Messages] Send error:", err);
      toast.error("Failed to send SMS. Check your connection.");
    }
    setSending(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Derived state ──────────────────────────────────────────────

  const selectedConversation =
    conversations.find((c) => c.leadId === selectedId) ??
    (newConversation && newConversation.leadId === selectedId ? newConversation : undefined);
  const isAccountManager = selectedId === "account-manager";

  // ── Render ─────────────────────────────────────────────────────

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-12rem)] flex flex-col overflow-x-hidden">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <div>
            <h1 className="font-display text-2xl font-bold text-foreground">Messages</h1>
            {!isMobile && (
              <p className="text-sm text-muted-foreground">
                Your conversations with leads and account manager
              </p>
            )}
          </div>
        </div>

        <div className="flex-1 flex gap-0 sm:gap-6 min-h-0">
          {/* ── Conversation List ─────────────────────────────── */}
          <div className={`${isMobile ? 'w-full' : 'w-80'} bg-card rounded-2xl border border-border flex flex-col flex-shrink-0 ${isMobile && mobileShowChat ? 'hidden' : ''}`}>
            <div className="p-4 border-b border-border flex items-center justify-between">
              <p className="text-sm font-semibold text-foreground">Conversations</p>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                title="New message"
                onClick={() => {
                  setShowNewMessagePicker(!showNewMessagePicker);
                  if (!showNewMessagePicker) fetchAvailableLeads();
                }}
              >
                {showNewMessagePicker ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              </Button>
            </div>

            {/* New Message Picker */}
            {showNewMessagePicker && (
              <div className="border-b border-border bg-muted/30">
                <div className="p-3">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Search leads..."
                      value={leadSearchQuery}
                      onChange={(e) => setLeadSearchQuery(e.target.value)}
                      className="pl-8 h-8 text-sm"
                    />
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {loadingLeads ? (
                    <div className="flex items-center justify-center py-4 text-muted-foreground">
                      <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />
                      <span className="text-xs">Loading leads...</span>
                    </div>
                  ) : availableLeads.filter((l) =>
                      !leadSearchQuery ||
                      l.leadName.toLowerCase().includes(leadSearchQuery.toLowerCase()) ||
                      l.leadState.toLowerCase().includes(leadSearchQuery.toLowerCase()) ||
                      l.leadType.toLowerCase().includes(leadSearchQuery.toLowerCase())
                    ).length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-4">
                      {availableLeads.length === 0
                        ? "All your leads already have conversations."
                        : "No leads match your search."}
                    </p>
                  ) : (
                    availableLeads
                      .filter((l) =>
                        !leadSearchQuery ||
                        l.leadName.toLowerCase().includes(leadSearchQuery.toLowerCase()) ||
                        l.leadState.toLowerCase().includes(leadSearchQuery.toLowerCase()) ||
                        l.leadType.toLowerCase().includes(leadSearchQuery.toLowerCase())
                      )
                      .map((lead) => (
                        <button
                          key={lead.leadId}
                          onClick={() => {
                            const leadData = {
                              id: lead.leadId,
                              lead_type_name: lead.leadType,
                              state: lead.leadState,
                              lead_profile: { phone: lead.leadPhone, first_name: lead.leadName.split(" ")[0], last_name: lead.leadName.split(" ").slice(1).join(" ") },
                            };
                            createNewConversationFromLead(leadData);
                          }}
                          className="w-full text-left px-3 py-2.5 hover:bg-muted/50 transition-colors flex items-center gap-2.5"
                        >
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-foreground font-semibold text-xs flex-shrink-0">
                            {getInitials(lead.leadName)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{lead.leadName}</p>
                            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                              <span>{lead.leadType}</span>
                              <span>{lead.leadState}</span>
                              {lead.leadPhone && <span>{formatPhoneDisplay(lead.leadPhone)}</span>}
                            </div>
                          </div>
                        </button>
                      ))
                  )}
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto">
              {/* Account Manager — always pinned at top */}
              <button
                onClick={() => selectConversation("account-manager")}
                className={`w-full text-left p-4 border-b border-border transition-colors ${
                  isAccountManager
                    ? "bg-accent/10 border-l-2 border-l-accent"
                    : "hover:bg-muted/50 border-l-2 border-l-transparent"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold flex-shrink-0">
                    <Headphones className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-foreground text-sm truncate">
                        {amInfo.name}
                      </p>
                      <span className="px-1.5 py-0.5 bg-primary/10 text-primary text-[10px] font-medium rounded-full flex-shrink-0">
                        Support
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      Your dedicated support channel
                    </p>
                  </div>
                </div>
              </button>

              {/* Lead conversations */}
              {loadingConversations ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  <span className="text-sm">Loading...</span>
                </div>
              ) : conversations.length === 0 && !newConversation ? (
                <div className="p-4 text-center">
                  <MessageSquare className="w-6 h-6 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">
                    No lead conversations yet.
                    <br />
                    Send your first SMS from a lead detail page.
                  </p>
                </div>
              ) : (
                [...(newConversation && !conversations.some((c) => c.leadId === newConversation.leadId)
                  ? [newConversation]
                  : []),
                ...conversations].map((conv) => (
                  <button
                    key={conv.leadId}
                    onClick={() => selectConversation(conv.leadId)}
                    className={`w-full text-left p-4 border-b border-border transition-colors ${
                      selectedId === conv.leadId
                        ? "bg-accent/10 border-l-2 border-l-accent"
                        : "hover:bg-muted/50 border-l-2 border-l-transparent"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-foreground font-semibold text-sm flex-shrink-0">
                        {getInitials(conv.leadName)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-foreground text-sm truncate">
                            {conv.leadName}
                          </p>
                          <span className="text-[10px] text-muted-foreground flex-shrink-0 ml-2">
                            {formatRelativeTime(conv.lastTime)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                          {conv.messageCount === 0 ? (
                            <p className="text-xs text-primary italic truncate">
                              New conversation — send your first message
                            </p>
                          ) : (
                            <>
                              {conv.lastDirection === "outbound" && (
                                <span className="text-[10px] text-muted-foreground flex-shrink-0">You:</span>
                              )}
                              <p className="text-xs text-muted-foreground truncate">
                                {conv.lastMessage || "No message content"}
                              </p>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                            <FileText className="w-2.5 h-2.5" />
                            {conv.leadType}
                          </span>
                          <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                            <MapPin className="w-2.5 h-2.5" />
                            {conv.leadState}
                          </span>
                        </div>
                      </div>
                      {/* Indicator for last message being inbound (needs reply) */}
                      {conv.lastDirection === "inbound" && (
                        <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" title="Needs reply" />
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>

            <div className="p-3 border-t border-border">
              <p className="text-[10px] text-muted-foreground text-center">
                {conversations.length + (newConversation && !conversations.some((c) => c.leadId === newConversation.leadId) ? 1 : 0)} lead conversation{conversations.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>

          {/* ── Chat View ─────────────────────────────────────── */}
          <div className={`flex-1 bg-card rounded-2xl border border-border flex flex-col min-h-0 ${isMobile && !mobileShowChat ? 'hidden' : ''}`}>
            {isAccountManager ? (
              /* ── Account Manager Chat ───────────────────────── */
              amInfo.id ? (
                <>
                  {isMobile && (
                    <div className="p-2 border-b border-border">
                      <Button variant="ghost" size="sm" onClick={() => setMobileShowChat(false)}>
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Back
                      </Button>
                    </div>
                  )}
                  <AccountManagerChat
                    amId={amInfo.id}
                    amName={amInfo.name}
                    amInitials={amInfo.initials}
                  />
                </>
              ) : (
                /* ── No AM assigned fallback ───────────────────── */
                <>
                  <div className="p-4 border-b border-border flex items-center gap-3">
                    {isMobile && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 -ml-1" onClick={() => setMobileShowChat(false)}>
                        <ArrowLeft className="w-4 h-4" />
                      </Button>
                    )}
                    <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold">
                      <Headphones className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{amInfo.name}</p>
                      <p className="text-xs text-muted-foreground">Trusted James Support</p>
                    </div>
                  </div>
                  <div className="flex-1 flex items-center justify-center p-6">
                    <div className="text-center max-w-sm">
                      <Headphones className="w-12 h-12 text-muted-foreground/20 mx-auto mb-4" />
                      <p className="text-sm text-muted-foreground">
                        Your dedicated account manager will be assigned shortly. Once assigned, you'll be able to chat directly here.
                      </p>
                    </div>
                  </div>
                </>
              )
            ) : selectedConversation ? (
              /* ── Lead Conversation View ────────────────────── */
              <>
                {/* Chat Header with lead info */}
                <div className="p-4 border-b border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isMobile && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 -ml-1" onClick={() => setMobileShowChat(false)}>
                          <ArrowLeft className="w-4 h-4" />
                        </Button>
                      )}
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-foreground font-semibold text-sm">
                        {getInitials(selectedConversation.leadName)}
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{selectedConversation.leadName}</p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            {selectedConversation.leadType}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {selectedConversation.leadState}
                          </span>
                          {selectedConversation.leadPhone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {formatPhoneDisplay(selectedConversation.leadPhone)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {!isMobile && (
                      <Link
                        to={`/dashboard/leads/${selectedConversation.leadId}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-primary hover:bg-primary/10 transition-colors"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        View Lead Details
                      </Link>
                    )}
                  </div>
                </div>

                {/* Messages */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                  {loadingMessages ? (
                    <div className="flex items-center justify-center py-8 text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Loading messages...
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center py-8">
                      <MessageSquare className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">
                        {selectedConversation?.messageCount === 0
                          ? "Start the conversation — type your message below."
                          : "No messages in this conversation."}
                      </p>
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.direction === "outbound" ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[75%] rounded-xl px-3.5 py-2.5 ${
                            msg.direction === "outbound"
                              ? "bg-primary text-primary-foreground rounded-br-sm"
                              : "bg-muted text-foreground rounded-bl-sm"
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap break-words">{msg.body}</p>
                          <div
                            className={`flex items-center gap-1 mt-1 ${
                              msg.direction === "outbound" ? "justify-end" : "justify-start"
                            }`}
                          >
                            <span
                              className={`text-[10px] ${
                                msg.direction === "outbound"
                                  ? "text-primary-foreground/60"
                                  : "text-muted-foreground"
                              }`}
                            >
                              {formatMessageTime(msg.created_at)}
                            </span>
                            {msg.direction === "outbound" && <StatusIcon status={msg.status} />}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Compose */}
                <div className="p-4 border-t border-border">
                  {selectedConversation.leadPhone ? (
                    <>
                      <div className="flex gap-2">
                        <Textarea
                          placeholder={`Message ${selectedConversation.leadName}...`}
                          value={body}
                          onChange={(e) => setBody(e.target.value)}
                          onKeyDown={handleKeyDown}
                          className="min-h-[44px] max-h-32 resize-none text-sm"
                          rows={1}
                          disabled={sending}
                        />
                        <Button
                          size="icon"
                          onClick={handleSend}
                          disabled={sending || !body.trim()}
                          className="h-[44px] w-[44px] flex-shrink-0"
                        >
                          {sending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      {body.trim() && (
                        <p className="text-[10px] text-muted-foreground mt-1.5">
                          Ctrl+Enter to send &middot; {body.length}/1600
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground italic text-center">
                      Cannot send SMS — this lead has no phone number.
                    </p>
                  )}
                </div>
              </>
            ) : (
              /* ── Empty State ───────────────────────────────── */
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageSquare className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Select a conversation to start messaging</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Messages;
